import { AsyncPipe } from '@angular/common'
import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe } from '@ngx-translate/core'
import { combineLatest, filter, map, shareReplay } from 'rxjs'
import { AnalyticsDashboardComponent } from '../analytics-dashboard/analytics-dashboard.component'
import { ClientOrganization, ClientsService } from '../clients.service'
import { MetricsService } from '../metrics.service'
import { scopedId } from '../multi-environment/environment-merge'
import { SELF_ENVIRONMENT } from '../multi-environment/support-environment'
import { MultiFilterComponent } from '../multi-filter/multi-filter.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { UsersService } from '../users.service'

@Component({
  selector: 'app-client-dashboard',
  standalone: true,
  imports: [ToolbarComponent, TranslatePipe, AnalyticsDashboardComponent, MultiFilterComponent, SpinnerComponent, AsyncPipe],
  templateUrl: './client-dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './client-dashboard.component.scss',
})
export class ClientDashboardComponent {
  route = inject(ActivatedRoute)
  router = inject(Router)
  clientsService = inject(ClientsService)
  usersService = inject(UsersService)
  metricsService = inject(MetricsService)

  currentUser$ = this.usersService.currentUser$
  client$ = this.clientsService.getCurrentUserClient().pipe(shareReplay(1))

  /**
   * Null while it is still loading as well as for a client with no contract recorded, which is the
   * same thing as far as the dashboard is concerned: neither offers the subscription period.
   */
  subscriptionUsage$ = this.metricsService.getSubscriptionUsage().pipe(
    map((result) => result.data ?? null),
    shareReplay(1)
  )

  organizations$ = this.client$.pipe(
    filter((result) => result.isSuccess),
    map((result) => result.data?.organizations ?? []),
    shareReplay(1)
  )

  private selectedOrganizationIdsFromUrl$ = this.route.queryParamMap.pipe(
    map((params) =>
      params
        .getAll('organizationIds')
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id))
    )
  )

  /**
   * An empty selection means the whole client, which is what this dashboard shows by default.
   *
   * Scoped to the environment serving this page, which is the only one a customer's dashboard ever
   * reads: the analytics screens are shared with support's cross-environment view, where the plain
   * row id would not say which environment's organization it means.
   */
  organizationIds$ = combineLatest([this.organizations$, this.selectedOrganizationIdsFromUrl$]).pipe(
    map(([organizations, selectedIds]) => {
      const organizationIdsOfClient = organizations.map((organization) => organization.id)
      const validSelectedIds = selectedIds.filter((id) => organizationIdsOfClient.includes(id))
      const selection = validSelectedIds.length > 0 ? validSelectedIds : organizationIdsOfClient
      return selection.map((organizationId) => scopedId(SELF_ENVIRONMENT, organizationId))
    }),
    shareReplay(1)
  )

  selectedOrganizationIds$ = combineLatest([this.organizations$, this.selectedOrganizationIdsFromUrl$]).pipe(
    map(([organizations, selectedIds]) => selectedIds.filter((id) => organizations.some((organization) => organization.id === id)))
  )

  displayOrganization(organization: ClientOrganization): string {
    return organization.name
  }

  displaySelectedOrganizations(organizations: ClientOrganization[]) {
    return (ids: number[]) =>
      organizations
        .filter((organization) => ids.includes(organization.id))
        .map((organization) => organization.name)
        .join(', ')
  }

  async onOrganizationChange(selectedIds: number[]) {
    await this.router.navigate([], {
      queryParams: selectedIds.length ? { organizationIds: selectedIds } : { organizationIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  async onOrganizationClear() {
    await this.router.navigate([], {
      queryParams: { organizationIds: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }
}
